import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { ChevronLeft, Clock, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const METHODS = [
  ['AE', 'AE (Acoustic Emission)'],
  ['ET', 'ET (Eddy Current)'],
  ['LT', 'LT (Leak Testing)'],
  ['MT', 'MT (Magnetic Particle)'],
  ['PCT', 'PCT (Pressure Change Testing)'],
  ['PT', 'PT (Liquid Penetrant)'],
  ['RT', 'RT (Radiographic)'],
  ['RVI', 'RVI (Remote Visual Inspection)'],
  ['UT', 'UT (Ultrasonic)'],
  ['UTT', 'UTT (Ultrasonic Thickness)'],
  ['VTG', 'VTG (Visual Testing General)'],
  ['VTW', 'VTW (Visual Testing Welds)'],
];

export default function NdtHoursTargetsPage() {
  const { api, user } = useAuth();
  const isAdmin = user?.role === 'ADMIN';
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState({});
  const [draft, setDraft] = useState({});  // method -> string input value

  const fetchTargets = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/lms/ndt-hours-targets');
      const map = {};
      (data || []).forEach(d => { map[d.method] = d.target_hours; });
      setDraft(Object.fromEntries(METHODS.map(([code]) => [code, map[code] != null ? String(map[code]) : ''])));
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load targets');
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => { fetchTargets(); }, [fetchTargets]);

  const handleSave = async (method) => {
    const raw = draft[method];
    const num = parseFloat(raw);
    if (Number.isNaN(num) || num < 0) {
      toast.error('Target must be a non-negative number');
      return;
    }
    setSaving(s => ({ ...s, [method]: true }));
    try {
      await api.put(`/lms/ndt-hours-targets/${method}`, { target_hours: num });
      toast.success(`${method} target updated`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save');
    } finally {
      setSaving(s => ({ ...s, [method]: false }));
    }
  };

  return (
    <div className="p-6 max-w-3xl mx-auto" data-testid="hours-targets-page">
      <Link to="/training/ndt" className="inline-flex items-center text-sm text-gray-600 hover:text-gray-900 mb-3">
        <ChevronLeft className="w-4 h-4 mr-1" /> Back to NDT Personnel
      </Link>
      <div className="flex items-center gap-3 mb-6">
        <div className="p-2 bg-[#005EF8]/10 rounded-lg">
          <Clock className="w-6 h-6 text-[#005EF8]" />
        </div>
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Trainee Hours Targets</h1>
          <p className="text-sm text-gray-500">Org-wide target hours per NDT method for trainees.</p>
        </div>
      </div>

      {!isAdmin && (
        <p className="text-xs text-amber-600 mb-4">View-only. Only Admins can change targets.</p>
      )}

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Targets by method</CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-10">
              <Loader2 className="w-6 h-6 animate-spin text-[#005EF8]" />
            </div>
          ) : (
            <div className="space-y-3" data-testid="hours-target-list">
              {METHODS.map(([code, label]) => (
                <div key={code} className="grid grid-cols-1 sm:grid-cols-[1fr_140px_auto] gap-3 items-end border-b pb-3 last:border-b-0">
                  <div>
                    <Label className="text-xs">{label}</Label>
                  </div>
                  <div>
                    <Input
                      type="number"
                      min="0"
                      step="0.5"
                      value={draft[code] ?? ''}
                      onChange={(e) => setDraft(d => ({ ...d, [code]: e.target.value }))}
                      placeholder="hrs"
                      disabled={!isAdmin}
                      data-testid={`hours-target-input-${code}`}
                    />
                  </div>
                  <Button
                    onClick={() => handleSave(code)}
                    size="sm"
                    disabled={!isAdmin || saving[code]}
                    data-testid={`hours-target-save-${code}`}
                  >
                    {saving[code] ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Save'}
                  </Button>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
